import React, { useEffect, useState, useMemo } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { Table, Loader, Text, Space, Divider, MediaQuery, Alert, TextInput } from "@mantine/core";
import { ReportRunner, PageHeader } from "../components/shared";
import { InfoCircle } from "tabler-icons-react";
import { FlexReport } from "@captiva/shared-types";


export function FlexReports({ companyId }: { companyId: string }) {
  const [reports, setReports] = useState<FlexReport[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);

  const filteredReports = useMemo(() => {
    if (!searchTerm.trim()) return reports;
    const term = searchTerm.toLowerCase();
    return reports.filter((report) => {
      const name = (report.name || "").toLowerCase();
      const desc = (report.description || "").toLowerCase();
      return name.includes(term) || desc.includes(term);
    });
  }, [reports, searchTerm]);

  useEffect(() => {
    if (!companyId) return;

    const fetchReports = async () => {
      setLoading(true);
      try {
        const q = query(collection(db, "flexReports"), where("companyId", "==", companyId), where("isActive", "==", true));
        const snapshot = await getDocs(q);
        const reportsData = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() } as FlexReport));

        // Sort by name
        reportsData.sort((a, b) => a.name.localeCompare(b.name));

        // console.log("MOD: reports", JSON.stringify(reportsData))

        setReports(reportsData);
      } catch (error) {
        console.error("Error fetching flex reports:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, [companyId]);

  const getModuleName = (report: FlexReport) => {
    if ((report as any).moduleMetadata?.originalModuleName) {
      return (report as any).moduleMetadata.originalModuleName;
    }
    if (report.moduleId === 'fleet-refuel-transaction-module') {
      return 'Fleet Refuel Transaction Module';
    }
    return 'Custom';
  };

  if (!companyId) return <Text>No company selected.</Text>;

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', width: '100%' }}>
      <Loader size="lg" color="cap-blue" />
    </div>
  );

  return (
    <>
      <PageHeader
        title="Flex Reports"
        subtitle="View and execute dynamic custom business module reports"
        action={
          <TextInput
            placeholder="Search reports by name or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.currentTarget.value)}
            style={{ width: "300px" }}
          />
        }
      />
      <Space h="sm" />

      {reports.length === 0 ? (
        <Alert title="No reports found" color="yellow" icon={<InfoCircle />} style={{ maxWidth: "600px", margin: "2rem auto" }}>
          No report configurations found. <br /> Contact us at{" "}
          <a href="mailto:admin@captivadata.com">admin@captivadata.com</a> to set up your custom reports.
        </Alert>
      ) : filteredReports.length === 0 ? (
        <Text size="sm" color="dimmed" style={{ margin: "2em auto", width: "100%", textAlign: "center" }}>
          No reports match your search.
        </Text>
      ) : (
            <Table striped highlightOnHover>
              <thead>
                <tr>
                  <th>Name</th>
                  <MediaQuery smallerThan="sm" styles={{ display: "none" }}>
                    <th>Description</th>
                  </MediaQuery>
                  <MediaQuery smallerThan="md" styles={{ display: "none" }}>
                    <th>Templates</th>
                  </MediaQuery>
                  <MediaQuery smallerThan="lg" styles={{ display: "none" }}>
                    <th>Format</th>
                  </MediaQuery>
                  <th>Module</th>
                  {/* <th>Active</th> */}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredReports.map((report) => (
                  <tr key={report.id}>
                    <td>{report.name}</td>
                    <MediaQuery smallerThan="sm" styles={{ display: "none" }}>
                      <td>{report.description || 'No description'}</td>
                    </MediaQuery>
                    <MediaQuery smallerThan="md" styles={{ display: "none" }}>
                      <td>{((report as any).includedTemplates || []).length}</td>
                    </MediaQuery>
                    <MediaQuery smallerThan="lg" styles={{ display: "none" }}>
                      <td>{report.renderingType || 'Unknown'}</td>
                    </MediaQuery>
                    <td>{getModuleName(report)}</td>
                    {/* <td>{report.isActive ? 'Yes' : 'No'}</td> */}
                    <td><ReportRunner reportConfig={report as unknown as Parameters<typeof ReportRunner>[0]["reportConfig"]} companyId={companyId} /></td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}

      <Space h="xl" />
    </>
  );
}

export default FlexReports;